import React from 'react';
import { Zap, Target, Shield, ChevronRight } from 'lucide-react';

/* ============================================================
   CONQUEST OF THE PLANET — centred cinematic overlay
   ============================================================ */

const RED = '#FF8618';
const SOFT_WHITE = 'var(--text-secondary)';

const headlineStyle = {
  fontFamily: '"Orbitron", "Rajdhani", sans-serif',
  fontWeight: 900,
  color: 'var(--text-primary)',
  textShadow: '0 2px 12px rgba(0,0,0,0.55), 0 4px 32px rgba(0,0,0,0.35)',
};

const FEATURES = [
  { Icon: Zap, label: 'FAST-PACED' },
  { Icon: Target, label: 'TACTICAL' },
  { Icon: Shield, label: 'STRATEGIC' },
];

const FeatureItem = ({ Icon, label }) => (
  <div className="flex items-center gap-2.5" data-testid={`gp-icon-${label.toLowerCase()}`}>
    <Icon className="w-5 h-5" strokeWidth={1.6} style={{ color: RED }} />
    <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.28em', color: 'var(--text-muted)' }}>{label}</span>
  </div>
);

const FeatureSeparator = () => (
  <span className="gameplay-feature-sep block w-px h-6" style={{ background: 'rgba(255,255,255,0.18)' }} />
);

const Gameplay = () => {
  return (
    <section
      id="gameplay"
      data-testid="gameplay-section"
      className="gameplay-section relative overflow-hidden flex items-center justify-center text-center"
      style={{
        minHeight: '100vh',
        backgroundImage: 'url(/backgrounds/conquest-planet-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* centre vignette — readability only, no panel */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(3,7,11,0.64) 0%, rgba(3,7,11,0.35) 42%, rgba(3,7,11,0.08) 72%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="gameplay-content relative"
        data-testid="gameplay-content"
        style={{
          width: 'min(900px, calc(100% - 40px))',
          margin: '0 auto',
          padding: '70px 20px 50px',
          zIndex: 2,
        }}
      >
        {/* JOIN THE */}
        <div className="flex items-center justify-center gap-3 mb-3">
          <span className="block h-px w-6 sm:w-9" style={{ background: RED, boxShadow: '0 0 6px rgba(255,134,24,0.8)' }} />
          <span className="gameplay-eyebrow" style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, color: RED }}>JOIN THE</span>
          <span className="block h-px w-6 sm:w-9" style={{ background: RED, boxShadow: '0 0 6px rgba(255,134,24,0.8)' }} />
        </div>

        {/* CONQUEST */}
        <h2 className="gameplay-title-main" style={headlineStyle} data-testid="gameplay-title">CONQUEST</h2>

        {/* OF THE */}
        <div className="flex items-center justify-center gap-3 my-3">
          <span className="block h-px w-6 sm:w-9" style={{ background: RED }} />
          <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(0.95rem,1.6vw,1.3rem)', letterSpacing: '0.3em', color: RED }}>OF THE</span>
          <span className="block h-px w-6 sm:w-9" style={{ background: RED }} />
        </div>

        {/* PLANET */}
        <h2 className="gameplay-title-main" style={headlineStyle}>PLANET</h2>

        {/* divider with centre diamond */}
        <div className="flex items-center justify-center gap-3 mt-7 mb-8">
          <span className="block h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, transparent, #FF8618)' }} />
          <span className="block w-2 h-2 flex-shrink-0" style={{ background: RED, transform: 'rotate(45deg)', boxShadow: '0 0 8px rgba(255,134,24,0.6)' }} />
          <span className="block h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, #FF8618, transparent)' }} />
        </div>

        {/* body content */}
        <div className="mx-auto space-y-5" style={{ maxWidth: '700px' }}>
          <h3 style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(1.2rem,2vw,1.5rem)', color: RED }}>
            So... how far can you go?
          </h3>
          <p style={{ fontFamily: '"Rajdhani","Inter",sans-serif', fontWeight: 500, fontSize: 'clamp(16px, 1.1vw, 18px)', lineHeight: 1.5, color: SOFT_WHITE }}>
            Choose your race, explore a vast sci-fi world, and carve your own path through fast-paced multiplayer combat. No two battles play the same, and every victory feels earned.
          </p>
          <p style={{ fontFamily: '"Rajdhani","Inter",sans-serif', fontWeight: 500, fontSize: 'clamp(16px, 1.1vw, 18px)', lineHeight: 1.5, color: SOFT_WHITE }}>
            This is your chance to compete, experiment, and grow — until your race stands above all others.
          </p>
          <h3 style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: 'clamp(1.2rem,2vw,1.5rem)', color: RED }}>
            The planet is ready. Are you?
          </h3>
          <p style={{ fontFamily: '"Rajdhani","Inter",sans-serif', fontWeight: 400, fontSize: 'clamp(0.9rem,1.1vw,1rem)', fontStyle: 'normal', color: 'var(--text-muted)' }}>
            There&rsquo;s only one way to find out. Take your first drop.
          </p>
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-8">
          <button
            type="button"
            data-testid="gameplay-cta-btn"
            className="gameplay-cta-btn inline-flex items-center justify-between gap-3 px-6"
            style={{
              width: 'min(100%, 310px)',
              height: '58px',
              background: 'rgba(11,15,19,0.4)',
              border: '1px solid rgba(255,134,24,0.6)',
              borderRadius: '10px',
              cursor: 'pointer',
            }}
          >
            <span style={{ fontFamily: '"Rajdhani",sans-serif', fontWeight: 700, fontSize: '13px', letterSpacing: '0.26em', color: '#FFFFFF' }}>
              TAKE YOUR FIRST DROP
            </span>
            <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: RED }} />
          </button>
        </div>

        {/* feature row */}
        <div className="gameplay-features-row" data-testid="gameplay-icons">
          {FEATURES.map((f, idx) => (
            <React.Fragment key={f.label}>
              <FeatureItem Icon={f.Icon} label={f.label} />
              {idx < FEATURES.length - 1 && <FeatureSeparator />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Gameplay;
